import { useState, useRef, useEffect } from 'react'
import api from '@/lib/api'
import { Search, X, Database } from 'lucide-react'

export interface ProjectInfo {
  id: number
  project_code: string
  project_type: string
  client_name: string
  phone: string
  address: string
  service_expiry_date: string | null
}

interface Props {
  value: string
  onSelect: (project: ProjectInfo) => void
  onClear: () => void
}

export default function ProjectSearchSelect({ value, onSelect, onClear }: Props) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<ProjectInfo[]>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const timeout = useRef<NodeJS.Timeout>()

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  useEffect(() => {
    if (query.length < 2) {
      setResults([])
      return
    }
    clearTimeout(timeout.current)
    timeout.current = setTimeout(async () => {
      setLoading(true)
      try {
        const res = await api.get('/projects/search', { params: { q: query } })
        setResults(res.data)
        setOpen(true)
      } catch {
        setResults([])
      }
      setLoading(false)
    }, 300)
    return () => clearTimeout(timeout.current)
  }, [query])

  return (
    <div ref={ref} className="relative">
      <label className="text-sm font-medium text-slate-700 block mb-1">Project Code *</label>
      {value ? (
        <div className="flex items-center gap-2 w-full px-3 py-2 text-sm border rounded-lg bg-slate-50">
          <Database size={14} className="text-red-800 shrink-0" />
          <span className="flex-1 text-slate-900 font-medium">{value}</span>
          <button type="button" onClick={() => { onClear(); setQuery('') }} className="text-slate-400 hover:text-slate-600">
            <X size={14} />
          </button>
        </div>
      ) : (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
          <input
            type="text"
            value={query}
            onChange={e => { setQuery(e.target.value); setOpen(true) }}
            onFocus={() => { if (results.length) setOpen(true) }}
            placeholder="Type project code (e.g. CLAPJ 1234)..."
            className="w-full pl-8 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700"
          />
        </div>
      )}
      {open && !value && (
        <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-slate-200 max-h-56 overflow-y-auto">
          {loading ? (
            <div className="px-3 py-3 text-sm text-slate-400 text-center">Searching...</div>
          ) : results.length === 0 && query.length >= 2 ? (
            <div className="px-3 py-3 text-sm text-slate-400 text-center">No projects found</div>
          ) : (
            results.map(p => (
              <button key={p.id} type="button"
                onClick={() => { onSelect(p); setQuery(''); setOpen(false) }}
                className="w-full px-3 py-2.5 text-left text-sm hover:bg-red-50 border-b border-slate-100 last:border-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-red-900">{p.project_type}-{p.project_code}</span>
                  <span className="text-slate-500">{p.client_name}</span>
                </div>
                {p.service_expiry_date && (
                  <div className="text-xs text-slate-400 mt-0.5">Expiry: {p.service_expiry_date}</div>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}
